import { useState, useEffect } from 'react';
import { View, Text, ScrollView, ActivityIndicator, Platform, Pressable, Modal, Alert } from 'react-native';
import { useRouter } from 'expo-router';
import { useAuthStore } from '../../store/authStore';
import { useTheme } from '../../contexts/ThemeContext';
import { useStyles } from '../../hooks/useStyles';
import { useResponsive, getResponsiveValue } from '../../shared/utils/responsive';
import { authApi } from '../../shared/utils/api';
import { Button, PasswordInput } from '../../components/ui';
import { SectionCard, SummaryCard, Card } from '../../components/ui';
import { addColorOpacity } from '../../shared/utils/styles';
import { Ionicons } from '@expo/vector-icons';
import { PageContainer, PageHeader } from '../../components/layout';
import { logger } from '../../shared/utils/logger';
import { useAnalytics } from '../../hooks/useAnalytics';

// Supported counties by state - update when adding new regions
const SUPPORTED_REGIONS = {
  NC: {
    name: 'North Carolina',
    counties: [
      'Franklin',
      'Granville', 
      'Halifax',
      'Nash',
      'Northampton',
      'Vance',
      'Warren',
    ],
    note: 'Lake Gaston & surrounding area',
  },
};

// Release notes - update this when adding new features
// Most recent first
const RELEASE_NOTES = [
  {
    version: '0.9.0',
    date: '2024-12-26',
    title: 'Advanced Analytics & Family Grouping',
    features: [
      {
        icon: 'color-palette-outline',
        title: 'Map Color Modes',
        description: 'Color parcels by Tier, Owner, or $/Acre value. See market patterns at a glance with value heat mapping.',
      },
      {
        icon: 'git-network-outline',
        title: 'Family & Entity Grouping',
        description: 'Automatically detect family relationships and corporate entities. See who controls the neighborhood.',
      },
      {
        icon: 'bar-chart-outline',
        title: 'Multi-Chart Analytics',
        description: 'Three chart views: Size vs Price scatter, $/Acre distribution histogram, and bucket comparison bars.',
      },
      {
        icon: 'resize-outline',
        title: 'Size-Based Analysis',
        description: 'Compare your property against similar-sized parcels. Small, medium, large, and XL buckets.',
      },
      {
        icon: 'cash-outline',
        title: 'Ask vs Market Comparison',
        description: 'See how your asking price compares to median $/acre in your size bucket and overall market.',
      },
      {
        icon: 'people-circle-outline',
        title: 'Owner Concentration',
        description: 'Discover which owners control the most land. Click to highlight all their parcels on the map.',
      },
    ],
  },
  {
    version: '0.8.0',
    date: '2024-12-25',
    title: 'Parcel Intelligence & Neighbor Discovery',
    features: [
      {
        icon: 'map-outline',
        title: 'Parcel Boundary Overlays',
        description: 'See exact property boundaries on satellite maps. Color-coded tiers show your property and neighbors.',
      },
      {
        icon: 'people-outline',
        title: 'Neighbor Discovery',
        description: 'Automatically discover neighboring landowners. See who owns adjacent properties and beyond.',
      },
      {
        icon: 'stats-chart-outline',
        title: 'Neighborhood Analytics',
        description: 'Get insights on total acres, unique owners, and largest landowners in your area.',
      },
      {
        icon: 'link-outline',
        title: 'One-Click Parcel Linking',
        description: 'Link properties to county parcel records with one click. Auto-fetches from NC OneMap.',
      },
      {
        icon: 'document-text-outline',
        title: 'Tax Assessment Data',
        description: 'View tax values, land use classifications, and parcel IDs directly from county records.',
      },
      {
        icon: 'search-outline',
        title: 'Smart Neighbor Search',
        description: 'Search and filter neighbors by owner name, address, acreage, or value. Sort by any field.',
      },
    ],
  },
  {
    version: '0.7.0',
    date: '2024-12-24',
    title: 'Property Mapping',
    features: [
      {
        icon: 'map-outline',
        title: 'Interactive Property Maps',
        description: 'See your properties on a map. Status-colored markers show owned, researching, and more.',
      },
      {
        icon: 'location-outline',
        title: 'Address Autocomplete',
        description: 'Search for addresses with autocomplete. Mapbox geocoding auto-fills property location.',
      },
      {
        icon: 'globe-outline',
        title: 'Portfolio Overview Map',
        description: 'View all properties in your portfolio on one map. Auto-zoom to fit all locations.',
      },
      {
        icon: 'resize-outline',
        title: 'Property Details',
        description: 'Track acreage and county for each property. See it all in the property overview.',
      },
    ],
  },
  {
    version: '0.6.0',
    date: '2024-12-23',
    title: 'Smart Contact Intelligence',
    features: [
      {
        icon: 'people-circle-outline',
        title: 'Partnership-Amplified Contacts',
        description: 'See contacts from you AND your partners. Share service providers across the partnership.',
      },
      {
        icon: 'call-outline',
        title: 'Quick Contacts in Due Diligence',
        description: 'Need a surveyor? See your and partners\' contacts right in the checklist item.',
      },
      {
        icon: 'business-outline',
        title: 'Property Contact Management',
        description: 'New Contacts tab on property pages. Add property-specific contacts shared with partners.',
      },
      {
        icon: 'flash-outline',
        title: 'One-Tap Actions',
        description: 'Call or email contacts directly from the checklist. No more searching.',
      },
    ],
  },
  {
    version: '0.5.0',
    date: '2024-12-23',
    title: 'Due Diligence System',
    features: [
      {
        icon: 'clipboard-outline',
        title: 'Comprehensive Due Diligence Checklist',
        description: 'New module-based system with 20+ categories covering land basics, water, utilities, access, and more.',
      },
      {
        icon: 'flag',
        title: 'Smart Flag System',
        description: 'Flag items as Big Red, Red, Yellow, or Green flags. System suggests flags based on your data.',
      },
      {
        icon: 'documents-outline',
        title: 'Data Fields & Auto-Fill',
        description: 'Structured data entry for each checklist item. Enter data once, get smart flag suggestions.',
      },
      {
        icon: 'people-outline',
        title: 'Partner Task Assignment',
        description: 'Assign checklist items to partners. Track who completed what.',
      },
    ],
  },
  {
    version: '0.4.0',
    date: '2024-12-20',
    title: 'Property Management',
    features: [
      {
        icon: 'calculator-outline',
        title: 'Mortgage Calculator',
        description: 'Calculate monthly payments, amortization schedules, and compare loan scenarios.',
      },
      {
        icon: 'cash-outline',
        title: 'Financial Tracking',
        description: 'Track income and expenses per property. See cash flow summaries.',
      },
    ],
  },
  {
    version: '0.3.0',
    date: '2024-12-15',
    title: 'Portfolio Collaboration',
    features: [
      {
        icon: 'share-outline',
        title: 'Partner Invitations',
        description: 'Invite partners to portfolios via email. Manage permissions and access.',
      },
      {
        icon: 'chatbubbles-outline',
        title: 'Discussion Threads',
        description: 'Comment on properties and portfolios. Have conversations with partners.',
      },
    ],
  },
];

// Coverage Section Component - Shows supported counties by state
function CoverageSection({ 
  tokens, 
  utils, 
  isMobile,
  isSmallMobile 
}: { 
  tokens: any; 
  utils: any; 
  isMobile: boolean;
  isSmallMobile: boolean;
}) {
  const totalCounties = Object.values(SUPPORTED_REGIONS).reduce(
    (sum, state) => sum + state.counties.length, 
    0
  );

  return (
    <View style={{ marginBottom: utils.spacing.xl }}>
      <SectionCard
        title="Parcel Data Coverage"
        subtitle={`${totalCounties} counties with full parcel intelligence`}
      >
        {Object.entries(SUPPORTED_REGIONS).map(([stateCode, state]) => (
          <View key={stateCode}>
            {/* State Header */}
            <View style={{
              flexDirection: 'row',
              alignItems: 'center',
              gap: utils.spacing.sm,
              marginBottom: utils.spacing.sm,
            }}>
              <View style={{
                width: isSmallMobile ? 28 : 32,
                height: isSmallMobile ? 28 : 32,
                borderRadius: utils.radius.sm,
                backgroundColor: addColorOpacity(tokens.color.primary, 0.15),
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                <Ionicons 
                  name="location" 
                  size={isSmallMobile ? 14 : 16} 
                  color={tokens.color.primary} 
                />
              </View>
              <View style={{ flex: 1 }}>
                <Text style={{
                  fontSize: isSmallMobile ? utils.typography.sizes.sm : utils.typography.sizes.base,
                  fontWeight: utils.typography.weights.semibold,
                  color: tokens.color.onBackground,
                  fontFamily: utils.typography.heading.fontFamily,
                }}>
                  {state.name}
                </Text>
                {state.note && (
                  <Text style={{
                    fontSize: isSmallMobile ? 10 : utils.typography.sizes.xs,
                    color: addColorOpacity(tokens.color.onSurface, 0.6),
                    fontFamily: utils.typography.body.fontFamily,
                  }}>
                    {state.note}
                  </Text>
                )}
              </View>
              <View style={{
                backgroundColor: addColorOpacity(tokens.color.success, 0.15),
                paddingHorizontal: utils.spacing.sm,
                paddingVertical: 2,
                borderRadius: utils.radius.full,
              }}>
                <Text style={{
                  fontSize: isSmallMobile ? 10 : 11,
                  fontWeight: utils.typography.weights.semibold,
                  color: tokens.color.success,
                  fontFamily: utils.typography.body.fontFamily,
                }}>
                  {state.counties.length} counties
                </Text>
              </View>
            </View>

            {/* Counties Grid */}
            <View style={{
              flexDirection: 'row',
              flexWrap: 'wrap',
              gap: utils.spacing.xs,
              marginLeft: isSmallMobile ? 36 : 40,
            }}>
              {state.counties.map((county) => (
                <View
                  key={county}
                  style={{
                    backgroundColor: addColorOpacity(tokens.color.onSurface, 0.06),
                    paddingHorizontal: isSmallMobile ? utils.spacing.sm : utils.spacing.md,
                    paddingVertical: isSmallMobile ? 4 : 6,
                    borderRadius: utils.radius.sm,
                  }}
                >
                  <Text style={{
                    fontSize: isSmallMobile ? 11 : utils.typography.sizes.xs,
                    color: tokens.color.onSurface,
                    fontFamily: utils.typography.body.fontFamily,
                    fontWeight: utils.typography.weights.medium,
                  }}>
                    {county}
                  </Text>
                </View>
              ))}
            </View>
          </View>
        ))}

        {/* Features included */}
        <View style={{
          marginTop: utils.spacing.lg,
          paddingTop: utils.spacing.md,
          borderTopWidth: 1,
          borderTopColor: tokens.color.border,
        }}>
          <Text style={{
            fontSize: isSmallMobile ? 10 : utils.typography.sizes.xs,
            color: addColorOpacity(tokens.color.onSurface, 0.6),
            fontFamily: utils.typography.body.fontFamily,
            marginBottom: utils.spacing.sm,
          }}>
            Available in covered counties:
          </Text>
          <View style={{
            flexDirection: 'row',
            flexWrap: 'wrap',
            gap: utils.spacing.md,
          }}>
            {[
              { icon: 'map-outline', label: 'Parcel boundaries' },
              { icon: 'people-outline', label: 'Neighbor discovery' },
              { icon: 'analytics-outline', label: 'Market analytics' },
              { icon: 'cash-outline', label: 'Tax values' },
            ].map((feature) => (
              <View 
                key={feature.label}
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: utils.spacing.xs,
                }}
              >
                <Ionicons 
                  name={feature.icon as any} 
                  size={isSmallMobile ? 12 : 14} 
                  color={tokens.color.primary} 
                />
                <Text style={{
                  fontSize: isSmallMobile ? 10 : 11,
                  color: tokens.color.onSurface,
                  fontFamily: utils.typography.body.fontFamily,
                }}>
                  {feature.label}
                </Text>
              </View>
            ))}
          </View>
        </View>
      </SectionCard>
    </View>
  );
}

// Paginated What's New Section Component
function WhatsNewSection({ 
  tokens, 
  utils, 
  isMobile,
  isSmallMobile 
}: { 
  tokens: any; 
  utils: any; 
  isMobile: boolean;
  isSmallMobile: boolean;
}) {
  const [currentPage, setCurrentPage] = useState(0);
  const itemsPerPage = 1; // Show one release at a time
  const totalPages = RELEASE_NOTES.length;
  
  const currentRelease = RELEASE_NOTES[currentPage];
  const isLatest = currentPage === 0;

  return (
    <View style={{ marginBottom: utils.spacing.xl }}>
      <SectionCard
        title="What's New"
        subtitle={`${currentPage + 1} of ${totalPages} updates`}
      >
        {/* Release Content */}
        <View>
          {/* Release Header */}
          <View style={{
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: utils.spacing.md,
            flexWrap: 'wrap',
            gap: utils.spacing.xs,
          }}>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: utils.spacing.sm, flexShrink: 1, minWidth: 0 }}>
              <View style={{
                backgroundColor: isLatest ? addColorOpacity(tokens.color.primary, 0.15) : addColorOpacity(tokens.color.onSurface, 0.08),
                paddingHorizontal: utils.spacing.sm,
                paddingVertical: utils.spacing.xs,
                borderRadius: utils.radius.sm,
                flexShrink: 0,
              }}>
                <Text style={{
                  fontSize: isSmallMobile ? 10 : utils.typography.sizes.xs,
                  fontWeight: utils.typography.weights.bold,
                  color: isLatest ? tokens.color.primary : tokens.color.onSurface,
                  fontFamily: utils.typography.body.fontFamily,
                }}>
                  {isLatest ? 'NEW' : `v${currentRelease.version}`}
                </Text>
              </View>
              <Text 
                numberOfLines={1}
                style={{
                  fontSize: isSmallMobile ? utils.typography.sizes.sm : utils.typography.sizes.base,
                  fontWeight: utils.typography.weights.semibold,
                  color: tokens.color.onBackground,
                  fontFamily: utils.typography.heading.fontFamily,
                  flexShrink: 1,
                }}>
                {currentRelease.title}
              </Text>
            </View>
            <Text style={{
              fontSize: isSmallMobile ? 10 : utils.typography.sizes.xs,
              color: addColorOpacity(tokens.color.onSurface, 0.5),
              fontFamily: utils.typography.body.fontFamily,
              flexShrink: 0,
            }}>
              {new Date(currentRelease.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
            </Text>
          </View>

          {/* Features */}
          <View style={{ gap: utils.spacing.sm }}>
            {currentRelease.features.map((feature, featureIndex) => (
              <View 
                key={featureIndex}
                style={{
                  flexDirection: 'row',
                  alignItems: 'flex-start',
                  gap: utils.spacing.sm,
                }}
              >
                <View style={{
                  width: isSmallMobile ? 24 : 28,
                  height: isSmallMobile ? 24 : 28,
                  borderRadius: utils.radius.sm,
                  backgroundColor: addColorOpacity(tokens.color.primary, 0.1),
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}>
                  <Ionicons 
                    name={feature.icon as any} 
                    size={isSmallMobile ? 12 : 14} 
                    color={tokens.color.primary} 
                  />
                </View>
                <View style={{ flex: 1, minWidth: 0 }}>
                  <Text 
                    numberOfLines={1}
                    style={{
                      fontSize: isSmallMobile ? utils.typography.sizes.xs : utils.typography.sizes.sm,
                      fontWeight: utils.typography.weights.medium,
                      color: tokens.color.onBackground,
                      fontFamily: utils.typography.body.fontFamily,
                      marginBottom: 2,
                    }}>
                    {feature.title}
                  </Text>
                  <Text 
                    numberOfLines={2}
                    style={{
                      fontSize: isSmallMobile ? 11 : utils.typography.sizes.xs,
                      color: addColorOpacity(tokens.color.onSurface, 0.7),
                      fontFamily: utils.typography.body.fontFamily,
                      lineHeight: (isSmallMobile ? 11 : utils.typography.sizes.xs) * 1.4,
                    }}>
                    {feature.description}
                  </Text>
                </View>
              </View>
            ))}
          </View>
        </View>

        {/* Pagination Controls */}
        {totalPages > 1 && (
          <View style={{
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginTop: utils.spacing.lg,
            paddingTop: utils.spacing.md,
            borderTopWidth: 1,
            borderTopColor: tokens.color.border,
          }}>
            <Pressable
              onPress={() => setCurrentPage(p => Math.max(0, p - 1))}
              disabled={currentPage === 0}
              style={({ pressed }) => ({
                flexDirection: 'row',
                alignItems: 'center',
                gap: utils.spacing.xs,
                paddingVertical: utils.spacing.xs,
                paddingHorizontal: utils.spacing.sm,
                borderRadius: utils.radius.sm,
                backgroundColor: pressed ? addColorOpacity(tokens.color.onSurface, 0.05) : 'transparent',
                opacity: currentPage === 0 ? 0.3 : 1,
              })}
            >
              <Ionicons name="chevron-back" size={16} color={tokens.color.primary} />
              <Text style={{
                fontSize: utils.typography.sizes.sm,
                color: tokens.color.primary,
                fontFamily: utils.typography.body.fontFamily,
                fontWeight: utils.typography.weights.medium,
              }}>
                Newer
              </Text>
            </Pressable>

            {/* Page Dots */}
            <View style={{ flexDirection: 'row', gap: utils.spacing.xs }}>
              {RELEASE_NOTES.slice(0, Math.min(5, totalPages)).map((_, idx) => (
                <Pressable
                  key={idx}
                  onPress={() => setCurrentPage(idx)}
                  style={{
                    width: currentPage === idx ? 16 : 8,
                    height: 8,
                    borderRadius: 4,
                    backgroundColor: currentPage === idx 
                      ? tokens.color.primary 
                      : addColorOpacity(tokens.color.onSurface, 0.2),
                  }}
                />
              ))}
              {totalPages > 5 && (
                <Text style={{
                  fontSize: 10,
                  color: addColorOpacity(tokens.color.onSurface, 0.5),
                  fontFamily: utils.typography.body.fontFamily,
                }}>
                  +{totalPages - 5}
                </Text>
              )}
            </View>

            <Pressable
              onPress={() => setCurrentPage(p => Math.min(totalPages - 1, p + 1))}
              disabled={currentPage === totalPages - 1}
              style={({ pressed }) => ({
                flexDirection: 'row',
                alignItems: 'center',
                gap: utils.spacing.xs,
                paddingVertical: utils.spacing.xs,
                paddingHorizontal: utils.spacing.sm,
                borderRadius: utils.radius.sm,
                backgroundColor: pressed ? addColorOpacity(tokens.color.onSurface, 0.05) : 'transparent',
                opacity: currentPage === totalPages - 1 ? 0.3 : 1,
              })}
            >
              <Text style={{
                fontSize: utils.typography.sizes.sm,
                color: tokens.color.primary,
                fontFamily: utils.typography.body.fontFamily,
                fontWeight: utils.typography.weights.medium,
              }}>
                Older
              </Text>
              <Ionicons name="chevron-forward" size={16} color={tokens.color.primary} />
            </Pressable>
          </View>
        )}
      </SectionCard>
    </View>
  );
}

export default function HomePage() {
  const user = useAuthStore((state) => state.user);
  const clearAuth = useAuthStore((state) => state.clearAuth);
  const router = useRouter();
  const { mode, toggleMode } = useTheme();
  const styles = useStyles();
  const { tokens, utils } = styles;
  const responsive = useResponsive();
  const { isMobile, isSmallMobile } = responsive;
  const { trackPageView, trackClick } = useAnalytics();

  const [portfoliosCount, setPortfoliosCount] = useState<number>(0);
  const [propertiesCount, setPropertiesCount] = useState<number>(0);
  const [activeCount, setActiveCount] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);
  const [changePasswordModal, setChangePasswordModal] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');

  // Track page view
  useEffect(() => {
    trackPageView('/home');
  }, [trackPageView]);
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [changePasswordLoading, setChangePasswordLoading] = useState(false);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      setLoading(true);
      
      const portfoliosResponse = await authApi.getPortfolios();
      const portfolios = portfoliosResponse.portfolios || [];
      setPortfoliosCount(portfolios.length);

      let totalProperties = 0;
      let activeProperties = 0;

      for (const portfolio of portfolios) {
        try {
          const propertiesResponse = await authApi.getProperties(portfolio.id);
          const properties = propertiesResponse.properties || [];
          totalProperties += properties.length;
          
          const active = properties.filter(p => p.status === 'owned').length;
          activeProperties += active;
        } catch (error) {
          logger.error('Error fetching properties for portfolio', error, 'home');
        }
      }

      setPropertiesCount(totalProperties);
      setActiveCount(activeProperties);
    } catch (error: any) {
      logger.error('Error loading stats', error, 'home');
      setPortfoliosCount(0);
      setPropertiesCount(0);
      setActiveCount(0);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    clearAuth();
    router.replace('/');
  };

  const handleChangePassword = async () => {
    if (!currentPassword || !newPassword || !confirmNewPassword) {
      Alert.alert('Error', 'Please fill in all fields');
      return;
    }

    if (newPassword !== confirmNewPassword) {
      Alert.alert('Error', 'New passwords do not match');
      return;
    }

    if (newPassword.length < 8) {
      Alert.alert('Error', 'Password must be at least 8 characters long');
      return;
    }

    setChangePasswordLoading(true);
    try {
      await authApi.changePassword({ currentPassword, newPassword });
      Alert.alert('Success', 'Password changed successfully');
      setChangePasswordModal(false);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmNewPassword('');
    } catch (error: any) {
      Alert.alert('Error', error.message || 'Failed to change password');
    } finally {
      setChangePasswordLoading(false);
    }
  };

  // Responsive values for typography - smaller on mobile for better fit
  const headingSize = isSmallMobile ? 22 : getResponsiveValue(24, 28, 30, 32);
  const sectionHeadingSize = isSmallMobile ? 18 : getResponsiveValue(20, 22, 24, 26);
  const bodySize = isSmallMobile ? 13 : getResponsiveValue(14, 15, 15, 16);
  const buttonSize = isSmallMobile ? 14 : getResponsiveValue(15, 16, 17, 18);

  return (
    <PageContainer>
          {/* Header */}
          <View 
            style={{ marginBottom: isMobile ? utils.spacing.lg : utils.spacing['2xl'] }}
          >
            <Text style={[
              {
                fontSize: headingSize,
                fontWeight: utils.typography.weights.bold,
                marginBottom: utils.spacing.xs,
                letterSpacing: -0.5,
                color: tokens.color.onBackground,
                fontFamily: utils.typography.heading.fontFamily,
                lineHeight: headingSize * 1.2,
                width: '100%',
              }
            ]}>
              Welcome back
            </Text>
            <Text 
              numberOfLines={1}
              ellipsizeMode="tail"
              style={[
              {
                fontSize: isSmallMobile ? 14 : getResponsiveValue(15, 16, 17, 18),
                color: tokens.color.onBackground,
                fontFamily: utils.typography.body.fontFamily,
                lineHeight: isSmallMobile ? 20 : getResponsiveValue(22, 24, 25, 26),
                width: '100%',
                ...Platform.select({
                  web: {
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  } as any,
                }),
              }
            ]}>
              {user?.email}
            </Text>
          </View>

      {/* Overview Section */}
      <View style={styles.section}>
        <SummaryCard
          title="Overview"
          subtitle="Your portfolio statistics"
          metrics={[
            {
              label: 'Portfolios',
              value: portfoliosCount,
              loading,
            },
            {
              label: 'Properties',
              value: propertiesCount,
              loading,
            },
            {
              label: 'Active',
              value: activeCount,
              loading,
            },
          ]}
          columns={3}
          style={{ marginBottom: utils.spacing.xl }}
        />
      </View>

      {/* Coverage Section */}
      <CoverageSection
        tokens={tokens}
        utils={utils}
        isMobile={isMobile}
        isSmallMobile={isSmallMobile}
      />

      {/* What's New Section */}
      <WhatsNewSection 
        tokens={tokens} 
        utils={utils} 
        isMobile={isMobile}
        isSmallMobile={isSmallMobile}
      />

      {/* Quick Actions Section */}
      <View style={styles.section}>
        <SectionCard
          title="Quick Actions"
          subtitle="Manage your portfolios and settings"
          style={{ marginBottom: utils.spacing.xl }}
        >
              <View style={{ width: '100%' }}>
                <Pressable
                  onPress={() => router.push('/(tabs)/portfolios')}
                  style={({ pressed }) => [
                    {
                      backgroundColor: tokens.color.primary,
                      minHeight: isSmallMobile ? 42 : getResponsiveValue(44, 48, 50, 52),
                      paddingHorizontal: isSmallMobile ? utils.spacing.lg : utils.spacing.xl,
                      paddingVertical: isSmallMobile ? utils.spacing.sm : utils.spacing.md,
                      borderRadius: utils.radius.md,
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginBottom: utils.spacing.md,
                      width: '100%',
                      ...Platform.select({
                        web: {
                          boxShadow: `0 6px 20px ${addColorOpacity(tokens.color.primary, 0.35)}`,
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          ':hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: `0 8px 28px ${addColorOpacity(tokens.color.primary, 0.45)}`,
                          },
                          ':active': {
                            transform: 'translateY(0px) scale(0.98)',
                          },
                        } as any,
                        default: {
                          shadowColor: tokens.color.primary,
                          shadowOffset: { width: 0, height: 6 },
                          shadowOpacity: 0.35,
                          shadowRadius: 20,
                          elevation: 8,
                          opacity: pressed ? 0.9 : 1,
                        },
                      }),
                    },
                  ]}
                >
                  <Text style={{
                    fontSize: buttonSize,
                    fontWeight: utils.typography.weights.semibold,
                    letterSpacing: 0.2,
                    color: tokens.color.onPrimary,
                    fontFamily: utils.typography.body.fontFamily,
                  }}>
                    View Portfolios
                  </Text>
                </Pressable>
              </View>
              <View style={{ width: '100%' }}>
                <Pressable
                  onPress={toggleMode}
                  style={({ pressed }) => [
                    {
                      backgroundColor: 'transparent',
                      borderWidth: 1,
                      borderColor: tokens.color.border,
                      minHeight: isSmallMobile ? 42 : getResponsiveValue(44, 48, 50, 52),
                      paddingHorizontal: isSmallMobile ? utils.spacing.lg : utils.spacing.xl,
                      paddingVertical: isSmallMobile ? utils.spacing.sm : utils.spacing.md,
                      borderRadius: utils.radius.md,
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexDirection: 'row',
                      gap: utils.spacing.sm,
                      width: '100%',
                      ...Platform.select({
                        web: {
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          ':hover': {
                            borderColor: tokens.color.primary,
                            backgroundColor: addColorOpacity(tokens.color.primary, 0.05),
                          },
                          ':active': {
                            transform: 'scale(0.98)',
                          },
                        } as any,
                        default: {
                          opacity: pressed ? 0.8 : 1,
                        },
                      }),
                    },
                  ]}
                >
                  <Ionicons 
                    name={mode === 'light' ? 'moon' : 'sunny'} 
                    size={20} 
                    color={tokens.color.onSurface} 
                  />
                  <Text style={{
                    fontSize: buttonSize,
                    fontWeight: utils.typography.weights.medium,
                    color: tokens.color.onSurface,
                    fontFamily: utils.typography.body.fontFamily,
                  }}>
                    Switch to {mode === 'light' ? 'dark' : 'light'} mode
                  </Text>
                </Pressable>
              </View>
        </SectionCard>
      </View>

      {/* Account Section */}
      <View style={styles.section}>
        <SectionCard
          title="Account"
          subtitle="Your account information"
        >
          <View style={{ marginBottom: utils.spacing.lg }}>
                  <Text style={[
                    {
                      fontSize: bodySize,
                      color: tokens.color.onBackground,
                      marginBottom: utils.spacing.xs,
                      fontFamily: utils.typography.body.fontFamily,
                    }
                  ]}>
                    Email Address
                  </Text>
                  <Text 
                    numberOfLines={0}
                    style={[
                      {
                        fontSize: getResponsiveValue(16, 16, 17, 17),
                        color: tokens.color.onSurface, 
                        fontWeight: utils.typography.weights.medium,
                        fontFamily: utils.typography.body.fontFamily,
                        width: '100%',
                        flexWrap: 'wrap',
                        ...Platform.select({
                          web: {
                            wordBreak: 'break-word',
                            overflowWrap: 'break-word',
                          } as any,
                        }),
                      }
                    ]}
                  >
                    {user?.email}
                  </Text>
          </View>
          <View>
            <Text style={[
              {
                fontSize: bodySize,
                color: tokens.color.onBackground,
                marginBottom: utils.spacing.xs,
                fontFamily: utils.typography.body.fontFamily,
              }
            ]}>
              Role
            </Text>
            <Text style={[
              {
                fontSize: getResponsiveValue(16, 16, 17, 17),
                color: tokens.color.onSurface, 
                fontWeight: utils.typography.weights.medium,
                fontFamily: utils.typography.body.fontFamily,
              }
            ]}>
              {user?.role === 'admin' ? 'Administrator' : 'User'}
            </Text>
          </View>
          <View style={{ 
                borderTopWidth: 1, 
            borderTopColor: tokens.color.border,
            paddingTop: utils.spacing.lg,
            marginTop: utils.spacing.md,
          }}>
            <View style={{ width: '100%', marginBottom: utils.spacing.md }}>
              <Pressable
                    onPress={() => {
                      setCurrentPassword('');
                      setNewPassword('');
                      setConfirmNewPassword('');
                      setChangePasswordModal(true);
                    }}
                    style={({ pressed }) => [
                      {
                        backgroundColor: 'transparent',
                        borderWidth: 1,
                        borderColor: tokens.color.border,
                        minHeight: isSmallMobile ? 42 : getResponsiveValue(44, 48, 50, 52),
                        paddingHorizontal: isSmallMobile ? utils.spacing.lg : utils.spacing.xl,
                        paddingVertical: isSmallMobile ? utils.spacing.sm : utils.spacing.md,
                        borderRadius: utils.radius.md,
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '100%',
                        ...Platform.select({
                          web: {
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            ':hover': {
                              borderColor: tokens.color.primary,
                              backgroundColor: addColorOpacity(tokens.color.primary, 0.05),
                            },
                            ':active': {
                              transform: 'scale(0.98)',
                            },
                          } as any,
                          default: {
                            opacity: pressed ? 0.8 : 1,
                          },
                        }),
                      },
                    ]}
                  >
                    <Text style={{
                      fontSize: buttonSize,
                      fontWeight: utils.typography.weights.medium,
                      color: tokens.color.onSurface,
                      fontFamily: utils.typography.body.fontFamily,
                    }}>
                      Change Password
                    </Text>
                  </Pressable>
                </View>
            <View style={{ width: '100%' }}>
              <Pressable
                    onPress={handleLogout}
                    style={({ pressed }) => [
                      {
                        backgroundColor: tokens.color.error,
                        minHeight: isSmallMobile ? 42 : getResponsiveValue(44, 48, 50, 52),
                        paddingHorizontal: isSmallMobile ? utils.spacing.lg : utils.spacing.xl,
                        paddingVertical: isSmallMobile ? utils.spacing.sm : utils.spacing.md,
                        borderRadius: utils.radius.md,
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '100%',
                        ...Platform.select({
                          web: {
                            boxShadow: `0 6px 20px ${addColorOpacity(tokens.color.error, 0.35)}`,
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            ':hover': {
                              transform: 'translateY(-2px)',
                              boxShadow: `0 8px 28px ${addColorOpacity(tokens.color.error, 0.45)}`,
                            },
                            ':active': {
                              transform: 'translateY(0px) scale(0.98)',
                            },
                          } as any,
                          default: {
                            shadowColor: tokens.color.error,
                            shadowOffset: { width: 0, height: 6 },
                            shadowOpacity: 0.35,
                            shadowRadius: 20,
                            elevation: 8,
                            opacity: pressed ? 0.9 : 1,
                          },
                        }),
                      },
                    ]}
                  >
                    <Text style={{
                      fontSize: buttonSize,
                      fontWeight: utils.typography.weights.semibold,
                      letterSpacing: 0.2,
                      color: tokens.color.onError,
                      fontFamily: utils.typography.body.fontFamily,
                    }}>
                      Sign out
                    </Text>
                  </Pressable>
                </View>
              </View>
        </SectionCard>
      </View>

      {/* Change Password Modal */}
      <Modal
        visible={changePasswordModal}
        transparent={true}
        animationType="none"
        onRequestClose={() => setChangePasswordModal(false)}
      >
        <View style={styles.modalOverlay}>
          <ScrollView style={styles.modalContent} contentContainerStyle={{ paddingBottom: utils.spacing['2xl'] }}>
            <Text style={styles.modalTitle}>Change Password</Text>
            <Text style={[styles.text('sm'), { marginBottom: utils.spacing.md, color: addColorOpacity(tokens.color.onSurface, 0.8) }]}>
              Password must be at least 8 characters and include uppercase, lowercase, and numbers.
            </Text>
            <PasswordInput
              label="Current Password"
              placeholder="Enter current password"
              value={currentPassword}
              onChangeText={setCurrentPassword}
              style={{ marginBottom: utils.spacing.md }}
            />
            <PasswordInput
              label="New Password"
              placeholder="Enter new password"
              value={newPassword}
              onChangeText={setNewPassword}
              style={{ marginBottom: utils.spacing.md }}
            />
            <PasswordInput
              label="Confirm New Password"
              placeholder="Confirm new password"
              value={confirmNewPassword}
              onChangeText={setConfirmNewPassword}
              style={{ marginBottom: utils.spacing.lg }}
            />
            <View style={styles.buttonRow}>
              <Button
                title="Cancel"
                onPress={() => {
                  setChangePasswordModal(false);
                  setCurrentPassword('');
                  setNewPassword('');
                  setConfirmNewPassword('');
                }}
                variant="outline"
                style={{ flex: 1 }}
              />
              <Button
                title={changePasswordLoading ? 'Changing...' : 'Change Password'}
                onPress={handleChangePassword}
                variant="primary"
                style={{ flex: 1 }}
                disabled={changePasswordLoading}
              />
            </View>
          </ScrollView>
        </View>
      </Modal>
    </PageContainer>
  );
}
